/**
 * URL slugs for the four booking-flow steps. The server's step allowlist consumes these
 * (server/index.js BOOKING_FLOW_STEPS), and a test in src/constants/pages.test.ts pins them
 * to the APP_ROUTES.BOOKING_FLOW_* route endings so the two can never drift apart.
 */
export const BOOKING_FLOW_STEP_SLUGS = {
  SELECT_SERVICE: 'valj-tjanst',
  SELECT_EMPLOYEE: 'valj-utovare',
  ADD_ONS: 'tillval',
  EXTRA_TIME: 'extra-tid',
} as const;

/** One of the four booking-flow step slugs (the `goToStep` step targets). */
export type BookingFlowStep = (typeof BOOKING_FLOW_STEP_SLUGS)[keyof typeof BOOKING_FLOW_STEP_SLUGS];

/**
 * Header back-button label key for each step — what a back control pointing at the step
 * shows. The add-ons and extra-time steps use `stepLabel`, not `title`: their titles are
 * question/instruction copy ("Några tillval?", "Hjälp oss planera"), not destination names.
 * Shared by the in-flow shell (BookingFlowPage) and the calendar page's back button.
 */
export const BOOKING_FLOW_STEP_BACK_LABEL_KEYS = {
  [BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE]: 'features.bookingSelection.SelectService.title',
  [BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE]: 'features.bookingSelection.SelectEmployeeList.title',
  [BOOKING_FLOW_STEP_SLUGS.ADD_ONS]: 'features.bookingSelection.SelectAddOns.stepLabel',
  [BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME]: 'features.bookingSelection.SelectExtraTime.stepLabel',
} as const;

/** Query-param keys carried across the booking-flow step pages. */
export const BOOKING_FLOW_QUERY_PARAM_KEYS = {
  /** Selected service ids. */
  SELECTED_SERVICES: 'ss',
  /** Selected resource (employee) id. */
  SELECTED_RESOURCE: 'sr',
  /** Recommended service ids surfaced to the customer. */
  RECOMMENDED_SERVICES: 'rs',
  /** Selected extra-time add-on id. */
  EXTRA_TIME: 'et',
} as const;

/**
 * Upper bound on how long `withBookingFlowRedesignFlag` waits for the Confidence flag to
 * resolve before treating the visitor as control and redirecting to the place page.
 * `isReady` from `useConfidenceFlag` can otherwise stay false forever if the Confidence
 * client never becomes ready (see useConfidenceFlag.tsx) — this is set slightly above the
 * Confidence client's own fetch timeout (2000ms, see useConfidenceExperiment.tsx).
 */
export const FLAG_READY_TIMEOUT_MS = 3000;

/**
 * Upper bound on how long `useBookingFlowSelection` waits for a place — via the `place` prop or
 * an already-populated `state.book.place` — before concluding none is coming and redirecting
 * (replace) to the place page. Without this bound, a booking-flow step page reached with no
 * place context at all (neither an SSR-hydrated `state.book.place` nor a `place` prop ever
 * arriving) would render an empty shell forever instead of recovering.
 */
export const PLACE_READY_TIMEOUT_MS = 3000;
